/**
 * TCF 考试特定的题目类型定义
 */

import {
  QuestionType,
  createExamQuestionTypeDefinition,
  createFieldDefinition,
  type ExamSpecificQuestionTypeDefinition,
} from '@examflow/core';

/**
 * TCF 听力题定义
 */
export const tcfListeningDefinition: ExamSpecificQuestionTypeDefinition =
  createExamQuestionTypeDefinition(
    'tcf_listening',
    QuestionType.SINGLE_CHOICE,
    'TCF 听力题',
    {
      requiredStimulus: ['audio'],
      // TCF 听力特有字段
      extraFields: {
        // TCF 听力分为 3 大题，每大题有不同题型
        sectionGroup: createFieldDefinition(
          'sectionGroup',
          'number',
          true,
          '大题编号 (1-3)',
          {
            validator: (value) => {
              const num = Number(value);
              if (num < 1 || num > 3) {
                return 'TCF 听力必须属于第 1-3 大题';
              }
              return true;
            },
          }
        ),
        // 具体题型
        questionFormat: createFieldDefinition(
          'questionFormat',
          'string',
          true,
          'TCF 听力题型格式',
          {
            validator: (value) => {
              const validFormats = [
                'image_selection', // 图片选择
                'sentence_completion', // 句子填空
                'dialogue_comprehension', // 对话理解
                'interview_comprehension', // 采访理解
                'announcement_comprehension', // 公告理解
                'discussion_comprehension', // 讨论理解
              ];
              return validFormats.includes(value as string)
                ? true
                : `不支持的 TCF 听力题型: ${value}，必须是以下之一: ${validFormats.join(', ')}`;
            },
          }
        ),
        // 音频时长（秒）
        audioDuration: createFieldDefinition(
          'audioDuration',
          'number',
          false,
          '音频时长（秒）'
        ),
        // 是否有暂停时间
        hasPause: createFieldDefinition(
          'hasPause',
          'boolean',
          false,
          '音频间是否有暂停时间',
          { defaultValue: true }
        ),
      },

      // TCF 听力约束
      constraints: {
        maxAudioDuration: 180, // 最大3分钟
        supportedFormats: ['mp3', 'wav'],
        requiresTranscript: false, // TCF 不提供原文
      },

      // 自定义验证
      customValidator: (question) => {
        const errors = [];

        // TCF 听力音频时长验证
        if (question.audioDuration) {
          if (question.audioDuration > 180) {
            errors.push({
              field: 'audioDuration',
              message: 'TCF 听力音频不能超过3分钟',
              code: 'TCF_AUDIO_TOO_LONG',
            });
          }
          if (question.audioDuration < 5) {
            errors.push({
              field: 'audioDuration',
              message: 'TCF 听力音频不能短于5秒',
              code: 'TCF_AUDIO_TOO_SHORT',
            });
          }
        }

        // 验证 sectionGroup 和 questionFormat 的匹配关系
        const sectionGroup = question.sectionGroup;
        const questionFormat = question.questionFormat;

        if (sectionGroup && questionFormat) {
          // 第一大题通常是图片题
          if (sectionGroup === 1 && !['image_selection', 'sentence_completion'].includes(questionFormat)) {
            errors.push({
              field: 'questionFormat',
              message: 'TCF 听力第1大题通常为图片选择或句子填空题',
              code: 'TCF_FORMAT_SECTION_MISMATCH',
            });
          }
        }

        return errors;
      },
    }
  );

/**
 * TCF 语言结构题定义（语法和词汇）
 */
export const tcfLanguageStructureDefinition: ExamSpecificQuestionTypeDefinition =
  createExamQuestionTypeDefinition(
    'tcf_language_structure',
    QuestionType.SINGLE_CHOICE,
    'TCF 语言结构题',
    {
      extraFields: {
        // 题目子类型
        subType: createFieldDefinition(
          'subType',
          'string',
          true,
          '题目子类型',
          {
            validator: (value) => {
              const validTypes = ['lexical', 'grammatical'];
              return validTypes.includes(value as string)
                ? true
                : `必须是 'lexical' (词汇) 或 'grammatical' (语法)`;
            },
          }
        ),
        // 考查的语言点
        languagePoint: createFieldDefinition(
          'languagePoint',
          'string',
          false,
          '考查的语言点描述'
        ),
      },

      constraints: {
        // TCF 语言结构题选项通常是 4 个
        expectedOptionCount: 4,
      },

      customValidator: (question) => {
        const errors = [];

        // 验证选项数量
        if (question.options && question.options.length !== 4) {
          errors.push({
            field: 'options',
            message: 'TCF 语言结构题通常有 4 个选项',
            code: 'TCF_OPTIONS_COUNT',
          });
        }

        return errors;
      },
    }
  );

/**
 * TCF 阅读题定义
 */
export const tcfReadingDefinition: ExamSpecificQuestionTypeDefinition =
  createExamQuestionTypeDefinition(
    'tcf_reading',
    QuestionType.SINGLE_CHOICE,
    'TCF 阅读理解题',
    {
      requiredStimulus: ['passage'],
      extraFields: {
        // 大题编号
        sectionGroup: createFieldDefinition(
          'sectionGroup',
          'number',
          true,
          '大题编号 (1-5)',
          {
            validator: (value) => {
              const num = Number(value);
              if (num < 1 || num > 5) {
                return 'TCF 阅读必须属于第 1-5 大题';
              }
              return true;
            },
          }
        ),
        // 阅读材料类型
        materialType: createFieldDefinition(
          'materialType',
          'string',
          true,
          '阅读材料类型',
          {
            validator: (value) => {
              const validTypes = [
                'message', // 便条、短信
                'announcement', // 公告
                'advertisement', // 广告
                'instruction', // 说明书
                'article', // 文章
                'correspondence', // 书信
                'press_article', // 报刊文章
              ];
              return validTypes.includes(value as string)
                ? true
                : `不支持的 TCF 阅读材料类型: ${value}`;
            },
          }
        ),
        // 材料字数
        wordCount: createFieldDefinition('wordCount', 'number', false, '阅读材料字数'),
      },

      constraints: {
        maxWordCount: 1000, // 最大1000词
      },

      customValidator: (question) => {
        const errors = [];

        // 验证阅读材料长度
        if (question.passage) {
          const wordCount = question.passage.split(/\s+/).length;
          if (wordCount > 1000) {
            errors.push({
              field: 'passage',
              message: 'TCF 阅读材料不能超过 1000 词',
              code: 'TCF_PASSAGE_TOO_LONG',
            });
          }
        }

        return errors;
      },
    }
  );

/** Default word windows for TCF Expression écrite tâches (product defaults). */
export const TCF_WRITING_WORD_WINDOWS: Record<
  1 | 2 | 3,
  { minWordCount: number; maxWordCount: number }
> = {
  1: { minWordCount: 60, maxWordCount: 120 },
  2: { minWordCount: 120, maxWordCount: 150 },
  3: { minWordCount: 120, maxWordCount: 180 },
};

/**
 * TCF 书面表达（可选）— 开放作答
 */
export const tcfWritingDefinition: ExamSpecificQuestionTypeDefinition =
  createExamQuestionTypeDefinition(
    'tcf_writing',
    QuestionType.FILL_BLANK,
    'TCF 写作题',
    {
      responseMode: 'open_ended',
      extraFields: {
        taskNumber: createFieldDefinition(
          'taskNumber',
          'number',
          true,
          'Tâche 编号 (1-3)',
          {
            validator: (value) => {
              const num = Number(value);
              if (num !== 1 && num !== 2 && num !== 3) {
                return 'TCF 写作必须是 Tâche 1、2 或 3';
              }
              return true;
            },
          }
        ),
        minWordCount: createFieldDefinition(
          'minWordCount',
          'number',
          false,
          '最低字数要求'
        ),
        maxWordCount: createFieldDefinition(
          'maxWordCount',
          'number',
          false,
          '最高字数要求'
        ),
      },

      constraints: {
        expectedTaskCount: 3,
      },

      customValidator: (question) => {
        const errors: Array<{ field: string; message: string; code: string }> = [];
        const taskNumber = Number(question.taskNumber);
        if (taskNumber !== 1 && taskNumber !== 2 && taskNumber !== 3) {
          return errors;
        }
        const window = TCF_WRITING_WORD_WINDOWS[taskNumber as 1 | 2 | 3];
        if (question.minWordCount != null && Number(question.minWordCount) < window.minWordCount) {
          errors.push({
            field: 'minWordCount',
            message: `TCF 写作 Tâche ${taskNumber} 最低字数要求为 ${window.minWordCount}`,
            code: 'TCF_WRITING_MIN_WORDS',
          });
        }
        if (
          question.maxWordCount != null &&
          Number(question.maxWordCount) > window.maxWordCount * 2
        ) {
          errors.push({
            field: 'maxWordCount',
            message: `TCF 写作 Tâche ${taskNumber} 最高字数不宜超过 ${window.maxWordCount * 2}`,
            code: 'TCF_WRITING_MAX_WORDS',
          });
        }
        return errors;
      },
    }
  );

/**
 * 所有 TCF 题目类型定义
 */
export const TCF_QUESTION_TYPE_DEFINITIONS: Record<
  string,
  ExamSpecificQuestionTypeDefinition
> = {
  [tcfListeningDefinition.id]: tcfListeningDefinition,
  [tcfLanguageStructureDefinition.id]: tcfLanguageStructureDefinition,
  [tcfReadingDefinition.id]: tcfReadingDefinition,
  [tcfWritingDefinition.id]: tcfWritingDefinition,
};

/**
 * 根据基础类型获取 TCF 题目类型定义
 */
export function getTCFQuestionTypeDefinition(
  baseType: QuestionType
): ExamSpecificQuestionTypeDefinition | undefined {
  return Object.values(TCF_QUESTION_TYPE_DEFINITIONS).find(
    (def) => def.baseType === baseType
  );
}

/**
 * 获取 TCF 题目类型的 Section 建议
 * 根据题目类型推荐应该属于哪个 Section
 */
export function getTCFSectionSuggestion(
  questionTypeId: string
): 'listening' | 'language_structures' | 'reading' | 'writing' | null {
  const map: Record<
    string,
    'listening' | 'language_structures' | 'reading' | 'writing'
  > = {
    [tcfListeningDefinition.id]: 'listening',
    [tcfLanguageStructureDefinition.id]: 'language_structures',
    [tcfReadingDefinition.id]: 'reading',
    [tcfWritingDefinition.id]: 'writing',
  };
  return map[questionTypeId] ?? null;
}
